import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Grid } from '@mui/material';
import { useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
import DashboardCardsView from './Components/Dashboard/Cards';
import DayWiseTrend from './Components/Dashboard/DayWiseTrend';
import AssetNotifications from './Components/Dashboard/AssetNotifications';
import TotalNotification from './Components/Dashboard/TotalNotifications';
import TireProfileHandler from 'handlers/tire-pressure';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import type {
  RuleNotifications,
  TPMSLeakageNotification,
} from 'types/notification.types';
import {
  filterNonEmptyArrays,
  getPreviousNotifications,
  getRecentNotifications,
  transformData,
  extractReason,
} from './util';

const DashboardView = () => {
  const location = useLocation();
  const accessToken = useAppSelector((state) => state.authReducer.accessToken);
  const tireProfileHandler = new TireProfileHandler();
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const { isLoadingDashboard, dashboard } = useAppSelector(
    (state) => state.tireProfileReducer
  );
  const { assets } = useAppSelector((state) => state.assetReducer);
  const enableDarkTheme = isDarkTheme(theme);
  const { id: customerId } = useAppSelector(
    (state) => state.persistedReducer
  ).customer;
  const tirePatterns = useAppSelector((state) => state.tirePatternReducer);

  const makeModelDetails = useAppSelector(
    (state) => state.makeModelDetailsReducer
  );

  const [metricType, setMetricType] = useState<string>('Trend');
  const [sortBy, setSortBy] = useState<string>('7');
  const [selectedAsset, setSelectedAsset] = useState<{
    bumperId: string;
    deviceId: string;
  } | null>(null);

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const handleChange = (event: any) => {
    setSortBy(event.target.value);
  };

  useEffect(() => {
    if (accessToken) {
      void tireProfileHandler.getFleetTireProfile();
      void tireProfileHandler.getDashboard();
    }
  }, [accessToken]);

  useEffect(() => {
    const bumperNumber = location.state?.data?.bumperNumber;
    if (bumperNumber && assets.length > 0) {
      const asset = assets.find((a) => a.bumperNumber === bumperNumber);
      if (asset) {
        setSelectedAsset({
          bumperId: asset.bumperNumber || '',
          deviceId: asset.device?.deviceId || '',
        });
      }
    }
  }, [location.state, assets]);

  const validData = filterNonEmptyArrays(dashboard.data);
  const fuelRate = dashboard.fuelRate.fleet[0]?.fuel_rate ?? 0;

  const assetBumpersIds = Object.keys(validData) ?? [];

  const assetsWithMakeModel = assetBumpersIds.map((item) => {
    const assetDetails = assets.find(
      (asset) => asset?.device?.deviceId === item
    );
    if (assetDetails) {
      return {
        bumperId: assetDetails.bumperNumber,
        makeModel: `${assetDetails.make}-${assetDetails.model}`,
        deviceId: item,
      };
    }
    return {
      bumperId: item,
      makeModel: '',
      deviceId: item,
    };
  });

  function suspendNotification(
    notification:
      | RuleNotifications
      | (TPMSLeakageNotification & { source: string }),
    note: string
  ) {
    // Check if it's a leakage notification
    const isLeakage =
      'source' in notification && notification.source === 'leakage';

    void tireProfileHandler.suspendTpmSNotification(
      notification.notificationId,
      false,
      isLeakage
        ? (notification as TPMSLeakageNotification).leakType?.toUpperCase() ??
            'LEAK'
        : extractReason(
            (notification as RuleNotifications).rule?.ruleName ?? 'TPMS'
          ).toUpperCase(),
      notification.asset?.bumperNumber ?? 'Unknown Bumper',
      note,
      isLeakage ? 'leakage' : 'notification'
    );
  }

  const transformedDailyData = transformData(validData);

  const notifications = getRecentNotifications(
    dashboard.notifications,
    Number(sortBy)
  );

  const prevNotifications = getPreviousNotifications(
    dashboard.notifications,
    Number(sortBy)
  );

  // Get leakage notifications filtered by date
  const leakageNotifications = getRecentNotifications(
    dashboard.tpmsLeakageNotifications || [],
    Number(sortBy)
  );

  const prevLeakageNotifications = getPreviousNotifications(
    dashboard.tpmsLeakageNotifications || [],
    Number(sortBy)
  );

  // Combine notifications with source markers for the table
  const combinedNotifications = [
    ...notifications.map((n) => ({ ...n, source: 'notification' })),
    ...leakageNotifications.map((n) => ({ ...n, source: 'leakage' })),
  ].sort(
    (a, b) =>
      new Date(b.alertStartTs).getTime() - new Date(a.alertStartTs).getTime()
  );

  return (
    <Box>
      {isLoadingDashboard ? (
        <SingleFadeLoader />
      ) : (
        <>
          <DashboardCardsView
            enableDarkTheme={enableDarkTheme}
            notifications={notifications}
            prevNotifications={prevNotifications}
            leakageNotifications={leakageNotifications}
            prevLeakageNotifications={prevLeakageNotifications}
            data={validData}
            fuelRate={fuelRate}
          />
          <Grid container spacing={2}>
            <Grid item lg={9} sm={12} md={12} xs={12}>
              <DayWiseTrend
                metricType={metricType}
                setMetricType={setMetricType}
                sortBy={sortBy}
                handleChange={handleChange}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                data={transformedDailyData}
                notifications={notifications}
                selectedAsset={selectedAsset}
              />
            </Grid>
            <Grid item lg={3} sm={12} md={12} xs={12}>
              <AssetNotifications
                enableDarkTheme={enableDarkTheme}
                data={assetsWithMakeModel}
                selectedAsset={selectedAsset}
                setSelectedAsset={setSelectedAsset}
                notifications={notifications}
              />
            </Grid>
          </Grid>
          <TotalNotification
            enableDarkTheme={enableDarkTheme}
            sortBy={sortBy}
            handleChange={handleChange}
            notifications={combinedNotifications}
            selectedDate={selectedDate}
            selectedAsset={selectedAsset}
            suspendNotification={suspendNotification}
            makeModelDetails={makeModelDetails}
            tirePatterns={tirePatterns}
            customerId={customerId}
          />
        </>
      )}
    </Box>
  );
};

export default DashboardView;
